/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	Avatar,
	BackgroundImage,
	Box,
	Group,
	Image,
	Stack,
	Tooltip,
} from '@mantine/core';

// Mantine
import {
	DataTableColumn,
	DataTableSortStatus,
	DataTable as NativeDataTable,
	DataTableProps as NativeDataTableProps,
} from 'mantine-datatable';
import './DatatableCustomHeaderStyle.css';

// Styles
import classes from './Datatable.module.css';

// Types
import { ReactNode } from 'react';
import { DatatableTypes } from './types';

// Cells
import { useTranslation } from 'react-i18next';
import { Badge } from '../Badge';
import { Tag } from '../Tag';
import { Text } from '../Text';
import { CellGlobalActions } from './cells/CellGlobalActions';
import { CellOffsetActions } from './cells/CellOffsetActions';
import { CellTags } from './cells/CellTags/CellTags';

import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { NOT_APPLICABLE } from '@config/constants/common';
import { Flex, NumberFormatter } from '@mantine/core';
import { IconQuestionMark } from '@tabler/icons-react';
import { truncateString } from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import clsx from 'clsx';
import { Button } from '../Button';

import { ROLES } from '@config/constants/roles';
import { useValidImage } from '@hooks/useValidImage';
import { useAuthorization } from '@lib/authorization';
import { IconProjectLocation } from '../IconProjectLocation/IconProjectLocation';
import { StatusSwitch } from '../StatusSwitch';
import { Title } from '../Title';
import { CellActions } from './cells/CellActions';
import { CellMintStatus } from './cells/CellMintStatus';
import { CellOverrangeStatus } from './cells/CellOverrangeStatus';
import { CellProductionActions } from './cells/CellProductionActions/CellProductionActions';
import { CellShowAction } from './cells/CellShowAction';
import { CellShowDeleteAction } from './cells/CellShowDeleteAction';

const DEFAULT_COVER =
	'https://res.cloudinary.com/inuk/image/upload/v1750255083/inuk/IGLU/usptbumtdwhypmghqxy3.jpg';

/* -------------------------------------------------------------------------- */
/*                                  Types                                     */
/* -------------------------------------------------------------------------- */
type ColumnType =
	| 'globalActions'
	| 'expand'
	| 'mintStatus'
	| 'tags'
	| 'offsetStatus'
	| 'verifiedStatus'
	| 'offsetsActions'
	| 'projectType'
	| 'availableStock'
	| 'projectStatus'
	| 'projectSellPrice'
	| 'projectButtonSeeMore'
	| 'project';

export type CustomColumn<T> = DataTableColumn<T> & {
	type?: any;
	defaultVisible?: boolean;
	fixed?: boolean;
};

type DataTableProps<T> = NativeDataTableProps<T> & {
	columns: CustomColumn<T>[];
	sortstatus?: DataTableSortStatus<T>;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Datatable = <T extends DatatableTypes>(
	props: DataTableProps<T> & {
		onSend?: (row: T) => void;
		onDownload?: (row: T) => void;
		onVerify?: (row: T) => void;
		onMint?: (row: T) => void;
		onDelete?: (row: T) => void;
		allColumns: any;
		handleUpdateColumns?: (cols: any[]) => void;
		onStatusProjectChange?: (status: boolean) => void;
	} & any
) => {
	const {
		columns,
		sortstatus,
		onSortStatusChange,
		allColumns,
		handleUpdateColumns,
		onStatusProjectChange,
		onSend,
		onDownload,
		emptyState,
		onVerify,
		...restProps
	} = props;

	const { t } = useTranslation();

	const { checkRole } = useAuthorization();

	const adaptedColumns =
		columns &&
		columns.map((column: CustomColumn<T>) => {
			const adaptedCol = { ...column };

			if (column.type) {
				switch (column.type) {
					case 'showAction':
						adaptedCol.render = (row: T): ReactNode => {
							return (
								<CellShowAction
									data={row}
									onShow={(e) => {
										e.stopPropagation();
										if (row && props.onShow) {
											props.onShow(row);
										}
									}}
								/>
							);
						};
						break;
					case 'showDeleteAction':
						adaptedCol.render = (row: T): ReactNode => {
							return (
								<CellShowDeleteAction
									data={row}
									onShow={(e) => {
										if (row && props.onShow) {
											props.onShow(row);
										}
									}}
									onDelete={(e) => {
										if (row && props.onDelete) {
											props.onDelete(row);
										}
									}}
								/>
							);
						};
						break;
					case 'commonActions':
						adaptedCol.render = (row: T): ReactNode => {
							return (
								<CellActions
									data={row}
									onShow={(e) => {
										if (row && props.onRowClick) {
											props.onRowClick({
												event: e,
												record: row,
												index: 1,
											});
										}
									}}
								/>
							);
						};
						break;
					case 'productionsActions':
						adaptedCol.render = (row: any): ReactNode => {
							return (
								<CellProductionActions
									onDelete={(e) => {
										e.stopPropagation();
										props.onDelete(row);
									}}
									isDeleted={!row.verified}
									isVerified={row.verified}
									isMinted={
										!row?.aco2Token && row.verified
									}
									onVerify={(e) => {
										e.stopPropagation();
										props.onMint(row);
									}}
									onShow={(e) => {
										if (row && props.onRowClick) {
											props.onRowClick({
												event: e,
												record: row,
												index: 1,
											});
										}
									}}
								/>
							);
						};
						break;
					case 'offsetsActions':
						adaptedCol.render = (row: T): ReactNode => {
							return (
								<CellOffsetActions
									data={row}
									onShow={(e) => {
										if (row && props.onRowClick) {
											props.onRowClick({
												event: e,
												record: row,
												index: 1,
											});
										}
									}}
									onDownload={async (e) => {
										e.stopPropagation();
										if (onDownload) await onDownload(row);
									}}
									onSend={(e) => {
										e.stopPropagation();
										if (onSend) onSend(row);
									}}
								/>
							);
						};
						break;
					case 'tags': {
						adaptedCol.render = function render(
							row: any
						) {
							return (
								<CellTags
									tags={row && row.tags ? row.tags : []}
								/>
							);
						};
						break;
					}
					case 'offsetStatus': {
						adaptedCol.render = function render(
							row: any
						) {
							return (
								<Tag
									type={row.status}
									label={row.status}
								/>
							);
						};
						break;
					}
					case 'verifiedStatus': {
						adaptedCol.render = function render(
							row: any
						) {
							return (
								<Box
									onClick={(e) => {
										e.stopPropagation();
									}}
								>
									<StatusSwitch
										disabled
										inActiveLabel={t('global.unverified')}
										activeLabel={t('global.verified')}
										active={row.verified}
										readonly={
											row?.status == 'MINTED' ||
											checkRole([
												ROLES.STAFF,
												ROLES.PRODUCER_STAFF,
												ROLES.PRODUCER_ADMIN,
												ROLES.ORGANIZATION_ADMIN,
											])
										}
										onChange={(event) => {
											onVerify(row);
										}}
									/>
								</Box>
							);
						};
						break;
					}

					case 'QuantityCheck': {
						adaptedCol.render = (row: any): ReactNode => {
							return <CellOverrangeStatus row={row} />;
						};
						break;
					}
					case 'mintStatus': {
						adaptedCol.render = function render(
							row: any
						) {
							return <CellMintStatus row={row} />;
						};
						break;
					}
					case 'activeStatus': {
						adaptedCol.render = function render(
							row: any
						) {
							return (
								<StatusSwitch
									inActiveLabel={t('global.inactive')}
									activeLabel={t('global.active')}
									active={row.verified}
									readonly={checkRole([
										ROLES.PRODUCER_STAFF,
										ROLES.PRODUCER_ADMIN,
										ROLES.ORGANIZATION_ADMIN,
									])}
								/>
							);
						};
						break;
					}
					case 'obfuscateIPFSStatus': {
						adaptedCol.render = function render(
							row: any
						) {
							return (
								<Group gap="4px">
									<Text
										className={clsx(
											classes.dot,
											!row.obfuscateIPFS
												? classes.successColor
												: classes.errorColor
										)}
									>
										●
									</Text>

									<Text
										className={clsx(
											classes.textObfuscateIPFS,
											!row.obfuscateIPFS
												? classes.successColor
												: classes.errorColor
										)}
									>
										{!row.obfuscateIPFS
											? t(`global.shared`)
											: t(`global.notShared`)}
									</Text>
								</Group>
							);
						};
						break;
					}
					case 'projectType': {
						adaptedCol.render = function render(
							row: any
						) {
							return t(row.type);
						};
						break;
					}
					case 'availableStock': {
						adaptedCol.render = function render(
							row: any
						) {
							return (
								<Badge
									type={
										row.availableStock
											? 'SUCCESS'
											: 'ERROR'
									}
								>
									<Text variant={'button'}>
										<NumberFormatter
											thousandSeparator=" "
											decimalSeparator=","
											value={row.availableStock}
											decimalScale={2}
										/>
										<Text size="11px" span>
											{` ${getUnitLabel('tco2e')}`}
										</Text>
									</Text>
								</Badge>
							);
						};
						break;
					}
					case 'projectStatus': {
						adaptedCol.render = function render(
							row: any
						) {
							return (
								<Box
									onClick={(e) => {
										e.stopPropagation();
									}}
								>
									<StatusSwitch
										inActiveLabel={'InActive'}
										activeLabel={'Active'}
										active={row?.active}
										readonly={false}
										onChange={(e) => {
											if (onStatusProjectChange && row)
												onStatusProjectChange(row);
										}}
									/>
								</Box>
							);
						};
						break;
					}
					case 'projectSellPrice': {
						adaptedCol.render = function render(
							row: any
						) {
							return (
								<Tooltip
									arrowSize={8}
									withArrow
									label={
										<Stack gap={'5px'}>
											<Flex
												gap={'15px'}
												justify={'space-between'}
												align={'center'}
											>
												<Text
													span
													variant={'caption'}
													c={'white'}
												>
													{t('global.partInuk')}
												</Text>
												<NumberFormatter
													thousandSeparator=" "
													decimalSeparator=","
													value={row?.sellPrice * 0.2}
													decimalScale={2}
													suffix="€"
												/>
											</Flex>
											<Flex
												gap={'15px'}
												justify={'space-between'}
												align={'center'}
											>
												<Text
													span
													variant={'caption'}
													c={'white'}
												>
													{t('global.partProject')}
												</Text>
												<NumberFormatter
													thousandSeparator=" "
													decimalSeparator=","
													value={row?.sellPrice * 0.4}
													decimalScale={2}
													suffix="€"
												/>
											</Flex>
											<Flex
												gap={'15px'}
												justify={'space-between'}
												align={'center'}
											>
												<Text
													span
													variant={'caption'}
													c={'white'}
												>
													{t('global.partAdditionality')}
												</Text>
												<NumberFormatter
													thousandSeparator=" "
													decimalSeparator=","
													value={row?.sellPrice * 0.4}
													decimalScale={2}
													suffix="€"
												/>
											</Flex>
										</Stack>
									}
								>
									<Box>
										<Title
											order={6}
											c={
												'var(--mantine-color-igluPrimary-9)'
											}
										>
											<NumberFormatter
												thousandSeparator=" "
												decimalSeparator=","
												value={row?.sellPrice}
												decimalScale={2}
											/>
											<Text
												span
												variant={'caption'}
												c={
													'var(--mantine-color-igluPrimary-9)'
												}
											>
												{` ${t('global.sellPriceUnit')}`}
											</Text>
										</Title>
									</Box>
								</Tooltip>
							);
						};
						break;
					}

					case 'projectButtonSeeMore': {
						adaptedCol.render = function render(
							_row: any
						) {
							return (
								<Button variant={'primary'} fullWidth>
									{t('global.learnMore')}
								</Button>
							);
						};
						break;
					}

					case 'project': {
						adaptedCol.render = function render(
							row: any
						) {
							const coverUrl = useValidImage(row?.image);
							return (
								<Flex gap={'20px'}>
									<BackgroundImage
										radius={'12px'}
										src={coverUrl}
										h={64}
										w={64}
									>
										<Flex>
											{row?.supportedBy && (
												<Image
													mt={'5px'}
													ml={'5px'}
													radius={'xl'}
													src={row?.supportedBy}
													h={20}
													w={20}
												/>
											)}
										</Flex>
									</BackgroundImage>

									<Flex
										direction={'column'}
										justify={'center'}
										gap={'3px'}
									>
										<Flex>
											<Text
												variant="subtitle1"
												c={
													'var(--mantine-color-igluTextPrimary-9)'
												}
											>
												{row.name}
											</Text>
										</Flex>
										<Flex gap={'16px'}>
											<Flex mih={'20px'}>
												<Group
													justify="center"
													gap={'5px'}
												>
													<Avatar
														radius={'xl'}
														src={`https://flagcdn.com/w40/${row.country?.code.toLowerCase()}.png`}
														size={18}
													>
														<IconQuestionMark size={18} />
													</Avatar>
													<Tooltip
														label={row?.country?.name}
														disabled={
															row?.country?.name
																?.length <= 11
														}
													>
														<Box>
															<Text
																variant={'caption'}
																c={
																	'var(--mantine-color-igluSecondary-9)'
																}
															>
																{truncateString(
																	row?.country?.name,
																	12
																)}
															</Text>
														</Box>
													</Tooltip>
												</Group>
											</Flex>
											<Flex
												style={{
													minHeight: '20px',
												}}
											>
												<Group gap={'5px'}>
													<IconProjectLocation
														color={''}
													/>
													<Tooltip
														label={row.location.place}
														disabled={
															row?.location?.place
																?.length <= 11
														}
													>
														<Box>
															<Text
																variant={'caption'}
																c={
																	'var(--mantine-color-igluSecondary-9)'
																}
															>
																{truncateString(
																	row?.location?.place,
																	12
																) || NOT_APPLICABLE}
															</Text>
														</Box>
													</Tooltip>
												</Group>
											</Flex>
										</Flex>
									</Flex>
								</Flex>
							);
						};
						break;
					}

					default:
						break;
				}
			}

			return adaptedCol.accessor === 'actions'
				? {
						...adaptedCol,
						title: (
							<div
								style={{
									width:
										adaptedCol.type == 'offsetsActions'
											? '105px'
											: 'fit-content',
								}}
							>
								<CellGlobalActions
									allColumns={allColumns}
									columns={columns}
									handlechangecolumns={(columns) => {
										if (handleUpdateColumns)
											handleUpdateColumns(columns);
									}}
								/>
							</div>
						),
					}
				: {
						...adaptedCol,
						title:
							typeof adaptedCol?.title === 'string'
								? t(adaptedCol.title)
								: (adaptedCol?.title ?? null),
					};
		});

	const handleemptystate = () => {
		return (
			<Box className={classes.containerTextsNoRecords}>
				<Image
					src={NoRecordsIcon}
					className={
						restProps.type == 'productionStartMint'
							? classes.iconNoRecordsMintHistory
							: classes.iconNoRecords
					}
				/>
				{restProps.type == 'productionStartMint' ? (
					<>
						<Text className={classes.titleNoRecords}>
							{t('global.datatable.noProductions')}
						</Text>
						<Text
							className={classes.descriptionNoRecords}
						>
							{t(
								'global.datatable.startBySelectingAproject'
							)}
						</Text>
					</>
				) : (
					<>
						<Text className={classes.titleNoRecords}>
							{t('global.datatable.noResultsFound')}
						</Text>
					</>
				)}
			</Box>
		);
	};

	return (
		<NativeDataTable
			id="V1StGXR8_Z5jdHi6B-datatable"
			height="calc(100vh - 310px)"
			highlightOnHover
			columns={adaptedColumns}
			classNames={classes}
			sortStatus={sortstatus}
			onSortStatusChange={onSortStatusChange}
			emptyState={handleemptystate()}
			{...restProps}
		/>
	);
};
